import {
  BasicOverlay,
  BasicOverlayData,
} from "$/game/prefabs/scenes/overlays/BasicOverlay.ts";
import { RoundedTextButton } from "$/game/prefabs/ui/RoundedTextButton.ts";
import { Pet } from "$/domain/pet/types.ts";

export type StatType = "energy" | "satiety" | "mood" | "clean";

export interface StatRestoreOverlayData extends BasicOverlayData {
  pet: Pet;
  stat: StatType;
}

export class StatRestoreOverlay extends BasicOverlay {
  constructor() {
    super({ key: "StatRestoreOverlay" });
  }

  override create(data: StatRestoreOverlayData) {
    // Call super.create with showCloseButton set to true
    super.create({ ...data, showCloseButton: true });

    const { width, height } = this.cameras.main;

    // Get stat-specific information
    const statInfo = this.getStatInfo(data.stat);

    // Stat indicator image
    const statIndicator = this.add.image(
      228,
      375,
      this.atlas,
      statInfo.indicatorAsset,
    );

    // Title text
    const titleText = this.add.text(
      300,
      350,
      statInfo.title,
      {
        fontSize: "40px",
        color: "#735933",
        fontFamily: "Phantom-Muff",
      },
    );
    titleText.setOrigin(0, 0);

    // Stat percentage text
    const statValue = data.pet[data.stat] || 0;
    const statText = this.add.text(
      width / 2,
      statIndicator.y + statIndicator.displayHeight / 2 + 30,
      `${statValue}% - 100%`,
      {
        fontSize: "40px",
        color: "#735933",
        fontFamily: "Phantom-Muff",
      },
    );
    statText.setOrigin(0.5, 0);

    const priceButton = new RoundedTextButton(
      this,
      width / 2,
      statIndicator.y + statIndicator.displayHeight / 2 + 150,
      "15  ",
      323,
      72,
      20,
      this.global.TUNER_TWA_CONFIG.palette.primary,
      this.global.TUNER_TWA_CONFIG.palette.primary + 0x100000,
      {
        fontSize: "40px",
        fontStyle: "normal",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    );

    // Add star UI image to the right of the text inside the button
    const eggUi = this.add.image(
      50,
      0,
      this.atlas,
      this.keys.assets.sprites.ui.crystalUi,
    );
    eggUi.setScale(0.8);
    // Make sure the star is part of the button by adding it to the button's container
    priceButton.add(eggUi);

    // Add click handler for the price button
    priceButton.on("pointerdown", async () => {
      // Check if pet has enough coins (at least 10)
      if (data.pet.coins < 15) {
        // Not enough coins, redirect to CurrencyShopScene
        // First close this overlay
        this.close();
        // Then transition from the parent scene
        if (data.fromScene) {
          const parentScene = this.scene.get(data.fromScene);
          if (parentScene && "transition" in parentScene) {
            (parentScene as any).transition.toScene(
              "CurrencyShopScene",
              this.keys.strings.statRestoreOverlay.notEnoughCoins,
            );
          }
        }
        return;
      }

      try {
        // Call the API to restore the specific stat
        const response = await this.restoreStat(data.stat);

        // If successful, show confetti and close this overlay
        if (response.data) {
          this.showConfetti();
          this.close();
        }
      } catch (error) {
        console.error(`Error restoring ${data.stat}:`, error);
        // Optionally show an error message to the user
      }
    });

    // Create a new "Restore with Ad" button below the price button
    const adRestoreButton = new RoundedTextButton(
      this,
      width / 2,
      statIndicator.y + statIndicator.displayHeight / 2 + 300, // Position below the price button
      this.keys.strings.statRestoreOverlay.restoreWithAd || "Restore with Ad",
      323,
      72,
      20,
      this.global.TUNER_TWA_CONFIG.palette.secondary, // Use secondary color
      this.global.TUNER_TWA_CONFIG.palette.secondary + 0x100000,
      {
        fontSize: "32px",
        fontStyle: "normal",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    );

    // Add click handler for the ad restore button
    adRestoreButton.on("pointerdown", () => {
      this.showAdForStatRestore(data.stat);
    });

    const adWarningText = this.add.text(
      width / 2,
      statIndicator.y + statIndicator.displayHeight / 2 + 360,
      this.keys.strings.statRestoreOverlay.adWarningText || "ad will be shown",
      {
        fontSize: "26px",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
        fontFamily: "Phantom-Muff",
      },
    );
    adWarningText.setOrigin(0.5, 0.5);
  }

  private getStatInfo(stat: StatType) {
    switch (stat) {
      case "energy":
        return {
          indicatorAsset: this.keys.assets.sprites.ui.indicatorEnergy,
          title: this.keys.strings.statRestoreOverlay.energy,
          apiMethod: this.api.pet.energyRestore,
        };
      case "satiety":
        return {
          indicatorAsset: this.keys.assets.sprites.ui.indicatorSatiety,
          title: this.keys.strings.statRestoreOverlay.satiety,
          apiMethod: this.api.pet.satietyRestore,
        };
      case "mood":
        return {
          indicatorAsset: this.keys.assets.sprites.ui.indicatorMood,
          title: this.keys.strings.statRestoreOverlay.mood,
          apiMethod: this.api.pet.moodRestore,
        };
      case "clean":
        return {
          indicatorAsset: this.keys.assets.sprites.ui.indicatorClean,
          title: this.keys.strings.statRestoreOverlay.clean,
          apiMethod: this.api.pet.cleanRestore,
        };
      default:
        // Fallback to energy
        return {
          indicatorAsset: this.keys.assets.sprites.ui.indicatorEnergy,
          title: this.keys.strings.statRestoreOverlay.energy,
          apiMethod: this.api.pet.energyRestore,
        };
    }
  }

  private async restoreStat(stat: StatType, byAd: boolean = false) {
    switch (stat) {
      case "energy":
        return await this.api.pet.energyRestore(byAd);
      case "satiety":
        return await this.api.pet.satietyRestore(byAd);
      case "mood":
        return await this.api.pet.moodRestore(byAd);
      case "clean":
        return await this.api.pet.cleanRestore(byAd);
      default:
        return await this.api.pet.energyRestore(byAd);
    }
  }

  private showConfetti(): void {
    try {
      const jsConfetti = new (window as any).JSConfetti();
      jsConfetti.addConfetti();
    } catch (error) {
      console.error("Error showing confetti:", error);
    }
  }

  private async showAdForStatRestore(stat: StatType): Promise<void> {
    try {
      if (this.telegram.RESTORE_STAT_ADS_CONTROLLER) {
        const result = await this.telegram.showAd("restoreStat");

        if (result) {
          try {
            const response = await this.restoreStat(stat, true);
            if (response.data) {
              this.showConfetti();
              this.close();
            }
          } catch (error) {
            console.error(`Error restoring ${stat} after ad:`, error);
          }
        } else {
          console.log("Ad was not watched to completion");
        }
      } else {
        console.error("Telegram plugin or ad controller not available");
      }
    } catch (error) {
      console.error("Error showing ad:", error);
    }
  }
}
